/* Simple mobile product galleries use native scrolling. Other carousels load
   Swiper only when visible or after an explicit product-card interaction. */
(() => {
  'use strict';
  const tag = document.currentScript;
  if (!tag || !tag.dataset.vendorSrc || !tag.dataset.utilitySrc) return;
  const requested = new Map();
  const watched = new WeakSet();
  const eligible = new WeakSet();
  const nativeStates = new WeakMap();
  const nativeGalleries = new Set();
  let loading;
  let loaded = false;
  let scanScheduled = false;

  function loadScript(src) {
    if (requested.has(src)) return requested.get(src);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = () => {
        requested.delete(src);
        script.remove();
        reject(new Error('Unable to load the gallery component'));
      };
      document.head.appendChild(script);
    });
    requested.set(src, promise);
    return promise;
  }

  function isSlider(element) {
    const desktop = window.matchMedia('(min-width: 1024px)').matches;
    return (element.dataset[desktop ? 'desktopLayout' : 'mobileLayout'] || 'slider') === 'slider';
  }

  function isCard(element) {
    return !!element.closest('.card__image');
  }

  function isNative(element) {
    return element.dataset.hoNativeGallery === 'true' && !element.hasAttribute('data-ho-native-upgraded') &&
      !window.matchMedia('(min-width: 1024px)').matches && isSlider(element) && !element.swiper?.initialized;
  }

  function nativeSlides(element) {
    return Array.from(element.children).filter(item => item.matches('swiper-slide'));
  }

  function renderNative(element) {
    // Defining Swiper adds an initially empty shadow root. Render its slots in
    // the same task so a different carousel's download cannot hide these images
    // while its utility script is still on the network.
    if (element.render && !element.rendered) element.render();
    element.closest('.main-product__media-wrapper')?.querySelectorAll('.swiper-thumbnails swiper-container').forEach(item => {
      if (item.render && !item.rendered) item.render();
    });
  }

  function startNative(element) {
    if (!isNative(element)) return false;
    if (nativeStates.has(element)) return true;
    const wrapper = element.closest('.main-product__media-wrapper');
    if (!wrapper) return false;
    const section = element.closest('.shopify-section');
    const thumbs = Array.from(wrapper.querySelectorAll('.swiper-thumbnails swiper-slide'));
    const previous = section?.querySelector(element.getAttribute('navigation-prev-el') || '.swiper-button-prev');
    const next = section?.querySelector(element.getAttribute('navigation-next-el') || '.swiper-button-next');
    const pagination = section?.querySelector(element.getAttribute('pagination-el') || '.swiper-pagination');
    const attributes = new Map();
    let frame = false;
    let moveTimeout;
    const state = { index: 0, media: undefined, wrapper, move, stop };
    const bullets = [];
    function remember(item, name, value) {
      if (!attributes.has(item)) attributes.set(item, new Map());
      const saved = attributes.get(item);
      if (!saved.has(name)) saved.set(name, item.getAttribute(name));
      item.setAttribute(name, value);
    }
    function update() {
      const slides = nativeSlides(element);
      state.index = Math.max(0, Math.min(slides.length - 1, Math.round(element.scrollLeft / (element.clientWidth + 16))));
      if (state.pendingIndex === state.index && Math.abs(element.scrollLeft - state.index * (element.clientWidth + 16)) < 2) cancelMove();
      state.media = slides[state.index]?.dataset.id;
      thumbs.forEach(item => remember(item, 'aria-current', String(item.dataset.id === state.media)));
      bullets.forEach((item, index) => item.setAttribute('aria-current', String(index === state.index)));
      if (pagination && element.getAttribute('pagination-type') === 'fraction') pagination.textContent = `${state.index + 1} / ${slides.length}`;
      [[previous, state.index === 0], [next, state.index >= slides.length - 1]].forEach(([item, disabled]) => {
        if (!item) return;
        item.classList.toggle('swiper-button-disabled', disabled);
        remember(item, 'aria-disabled', String(disabled));
      });
    }
    function scheduleUpdate() {
      if (frame) return;
      frame = true;
      requestAnimationFrame(() => { frame = false; if (nativeStates.has(element)) update(); });
    }
    function move(index, smooth = false) {
      const slides = nativeSlides(element);
      index = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[index];
      if (!slide) return;
      const left = slide.getBoundingClientRect().left - element.getBoundingClientRect().left + element.scrollLeft;
      clearTimeout(moveTimeout);
      state.pendingIndex = index;
      // scrollend covers interrupted native scrolling in current browsers. A
      // single bounded fallback releases intent in browsers without that event.
      moveTimeout = setTimeout(() => cancelMove(true), 800);
      element.scrollTo({ left, behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
      state.media = slide.dataset.id;
      if (smooth) scheduleUpdate();
      else update();
    }
    function action(event) {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('.swiper-button-next, .swiper-button-prev, .ho-native-gallery-bullet, .swiper-thumbnails swiper-slide');
      if (!control || !wrapper.contains(control)) return;
      if (event.type === 'keydown' && !['Enter', ' ', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const index = state.pendingIndex ?? state.index;
      if (control === next || event.key === 'ArrowRight') move(index + 1);
      else if (control === previous || event.key === 'ArrowLeft') move(index - 1);
      else if (control.dataset.hoNativeIndex) move(Number(control.dataset.hoNativeIndex));
      else move(nativeSlides(element).findIndex(item => item.dataset.id === control.dataset.id));
    }
    function keyboard(event) {
      if (event.target !== element || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      event.preventDefault();
      move((state.pendingIndex ?? state.index) + (event.key === 'ArrowRight' ? 1 : -1));
    }
    function focusNative(event) {
      if (event.target !== element) return;
      move(state.pendingIndex ?? state.index, false);
    }
    function cancelMove(updatePosition = false) {
      clearTimeout(moveTimeout);
      state.pendingIndex = undefined;
      if (updatePosition && nativeStates.has(element)) update();
    }
    function stop() {
      const media = nativeSlides(element)[state.pendingIndex ?? state.index]?.dataset.id;
      element.removeEventListener('scroll', scheduleUpdate);
      element.removeEventListener('keydown', keyboard);
      element.removeEventListener('focusin', focusNative);
      element.removeEventListener('pointerdown', cancelMove);
      element.removeEventListener('touchstart', cancelMove);
      element.removeEventListener('wheel', cancelMove);
      element.removeEventListener('scrollend', cancelMove);
      cancelMove();
      wrapper.removeEventListener('click', action, true);
      wrapper.removeEventListener('keydown', action, true);
      mutations.disconnect();
      resize?.disconnect();
      delete wrapper.dataset.hoNativeActive;
      bullets.forEach(item => item.remove());
      if (pagination && element.getAttribute('pagination-type') === 'fraction') pagination.textContent = '';
      attributes.forEach((saved, item) => saved.forEach((value, name) => value === null ? item.removeAttribute(name) : item.setAttribute(name, value)));
      [previous, next].forEach(item => item?.classList.remove('swiper-button-disabled'));
      nativeStates.delete(element);
      nativeGalleries.delete(element);
      element.scrollLeft = 0;
      return media;
    }
    [previous, next, ...thumbs].filter(Boolean).forEach(item => {
      remember(item, 'role', 'button');
      remember(item, 'tabindex', '0');
      if (!item.hasAttribute('aria-label')) remember(item, 'aria-label', item === previous ? 'Previous image' : item === next ? 'Next image' : `Show image ${thumbs.indexOf(item) + 1}`);
    });
    if (pagination && element.getAttribute('pagination-type') !== 'fraction') nativeSlides(element).forEach((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ho-native-gallery-bullet';
      button.dataset.hoNativeIndex = String(index);
      button.setAttribute('aria-label', `Show image ${index + 1}`);
      pagination.appendChild(button);
      bullets.push(button);
    });
    const order = nativeSlides(element);
    const mutations = new MutationObserver(() => {
      // The theme prepends the chosen variant's media before Swiper is present.
      // Live's Swiper slides to that photo instead, so put the photos back in
      // their order and scroll to the chosen one rather than downloading a slider.
      const slides = nativeSlides(element);
      const chosen = slides[0];
      if (element.dataset.variantStyle !== 'default' || !slides.length || slides.length !== order.length || !order.every(slide => slides.includes(slide))) {
        move(0, false);
        return;
      }
      const anchor = slides[slides.length - 1].nextSibling;
      if (slides.some((slide, index) => slide !== order[index])) order.forEach(slide => element.insertBefore(slide, anchor));
      mutations.takeRecords();
      move(order.indexOf(chosen), false);
    });
    let lastWidth = element.clientWidth;
    const resize = 'ResizeObserver' in window ? new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (!nativeStates.has(element) || width === lastWidth) return;
      lastWidth = width;
      move(state.pendingIndex ?? state.index, false);
    }) : null;
    mutations.observe(element, { childList: true });
    resize?.observe(element);
    nativeStates.set(element, state);
    nativeGalleries.add(element);
    renderNative(element);
    wrapper.dataset.hoNativeActive = 'true';
    remember(element, 'tabindex', '0');
    element.addEventListener('scroll', scheduleUpdate, { passive: true });
    element.addEventListener('keydown', keyboard);
    element.addEventListener('focusin', focusNative);
    element.addEventListener('pointerdown', cancelMove, { passive: true });
    element.addEventListener('touchstart', cancelMove, { passive: true });
    element.addEventListener('wheel', cancelMove, { passive: true });
    element.addEventListener('scrollend', cancelMove, { passive: true });
    wrapper.addEventListener('click', action, true);
    wrapper.addEventListener('keydown', action, true);
    update();
    return true;
  }

  function leaveNative(element) {
    const state = nativeStates.get(element);
    if (!state) return;
    const media = state.stop();
    if (!isSlider(element)) return;
    // Match the theme's preinitialisation variant path: keep the selected image
    // first during the download, including when that download fails and retries.
    const selected = nativeSlides(element).find(slide => slide.dataset.id === media);
    if (selected && selected !== element.firstElementChild) element.prepend(selected);
    element.dataset.hoNativeUpgraded = 'true';
    // Preserve the image chosen by swiping or thumbnail navigation at the
    // desktop breakpoint. The desktop component keeps its original settings.
    element.addEventListener('swiperafterinit', () => {
      // Swiper dispatches this inside its constructor, before element.swiper
      // receives the instance; wait until that assignment has completed.
      Promise.resolve().then(() => {
        const index = Array.from(element.swiper?.slides || []).findIndex(slide => slide.dataset.id === media);
        if (index >= 0) element.swiper.slideTo(index, 0);
      });
    }, { once: true });
  }

  window.customElements?.whenDefined('swiper-container').then(() => {
    nativeGalleries.forEach(element => { if (isNative(element)) renderNative(element); });
  });

  function allow(element) {
    eligible.add(element);
    // Keep the static first-slide fallback through both component downloads.
    // The utility lifts it synchronously when initialize() is actually called.
    const gallery = element.closest('.main-product__media-wrapper');
    if (gallery) gallery.querySelectorAll('.swiper-thumbnails swiper-container').forEach(item => eligible.add(item));
  }

  window.HOSliderShouldInitialize = element => eligible.has(element);
  window.HOEnsureSlider = element => {
    if (!element || !element.isConnected) return Promise.resolve();
    if (startNative(element)) return Promise.resolve(element);
    allow(element);
    if (loaded) {
      window.HOInitializeSliders?.();
      return Promise.resolve(element);
    }
    if (!loading) {
      loading = loadScript(tag.dataset.vendorSrc)
        .then(() => loadScript(tag.dataset.utilitySrc))
        .then(() => {
          loaded = true;
          window.HOInitializeSliders?.();
        })
        .catch(error => {
          loading = undefined; // A later interaction may retry a failed load.
          throw error;
        });
    }
    return loading.then(() => element);
  };

  function initialize(element) {
    if (!element.isConnected || !isSlider(element)) return;
    window.HOEnsureSlider(element).catch(error => console.warn(error.message));
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || !isSlider(entry.target)) return;
      observer.unobserve(entry.target);
      const element = entry.target;
      const start = () => initialize(element);
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1000 });
      else setTimeout(start, 0);
    });
  }, { rootMargin: '300px 0px' }) : null;

  function watch(element) {
    if (element.closest('.swiper-thumbnails')) return;
    // Detaching a native gallery removes its listeners. The same DOM element
    // can later reconnect, so restore those controls before the observation guard.
    if (startNative(element)) { watched.add(element); return; }
    if (watched.has(element)) return;
    watched.add(element);
    // Card galleries use the pre-initialisation variant-image path. They need
    // Swiper after a pointer, keyboard or variant action, not on grid paint.
    if (isCard(element)) return;
    if (observer) observer.observe(element);
    else if (isSlider(element)) initialize(element);
  }

  window.HORefreshSliders = (root = document) => {
    nativeGalleries.forEach(element => { if (!element.isConnected) nativeStates.get(element).stop(); });
    if (root.matches?.('swiper-container')) watch(root);
    root.querySelectorAll?.('swiper-container').forEach(watch);
    if (loaded) window.HOInitializeSliders?.();
  };

  function findSlider(target, type) {
    if (!(target instanceof Element)) return;
    const card = target.closest('.card');
    const section = target.closest('.shopify-section');
    const direct = target.closest('swiper-container');
    let element = direct || card?.querySelector('swiper-container');
    if (!element && section && target.closest('.swiper-button-thumbs-next, .swiper-button-thumbs-prev')) {
      element = section.querySelector('.swiper-thumbnails swiper-container');
    }
    if (!element && section && (type === 'variant-update' || target.closest('[class*="swiper-button"], .slideshow-text-controls__item'))) {
      element = section.querySelector('swiper-container[id^="gallery-"]') || section.querySelector('swiper-container');
    }
    return element;
  }
  function interaction(event) {
    const element = findSlider(event.target, event.type);
    if (!element) return;
    // Starting a card's (virtual) Swiper re-renders its slides. Keyboard focus on the photo link would drop to <body>,
    // and a mouse or pen press on it gets no click (the slide under the pointer is replaced before release), so the
    // product doesn't open. Focus or such a press inside the card slider leaves it unstarted; its title, swatches or
    // buttons start it as before, and touch still starts it on the press (taps still open the product).
    if (isCard(element) && element.contains(event.target) &&
      (event.type === 'focusin' || (event.type === 'pointerdown' && (event.pointerType === 'mouse' || event.pointerType === 'pen')))) return;
    if (startNative(element)) return;
    // Thumbnail intent belongs to the product gallery's native controls too.
    const gallery = element.closest('.main-product__media-wrapper')?.querySelector('swiper-container[data-ho-native-gallery]');
    if (gallery && startNative(gallery)) return;
    initialize(element);
  }
  ['pointerdown', 'focusin', 'variant-update'].forEach(name => document.addEventListener(name, interaction, true));

  // A customer can click an arrow while the first intent-triggered download is
  // still running. Replay that specific action once its listener is installed.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('.swiper-button-next, .swiper-button-prev, .swiper-button-thumbs-next, .swiper-button-thumbs-prev, .slideshow-text-controls__item');
    if (!control) return;
    const element = findSlider(control, 'click');
    if (!element || !isSlider(element) || element.swiper?.initialized) return;
    const gallery = element.closest('.main-product__media-wrapper')?.querySelector('swiper-container[data-ho-native-gallery]');
    if (startNative(element) || (gallery && startNative(gallery))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.HOEnsureSlider(element).then(() => {
      if (control.isConnected && element.swiper?.initialized) control.click();
    }).catch(error => console.warn(error.message));
  }, true);

  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    requestAnimationFrame(() => {
      scanScheduled = false;
      window.HORefreshSliders();
    });
  }
  new MutationObserver(records => {
    if (records.some(record => record.removedNodes?.length)) nativeGalleries.forEach(element => {
      if (!element.isConnected) nativeStates.get(element).stop();
    });
    if (records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches('swiper-container') || node.querySelector('swiper-container'))))) scheduleScan();
  }).observe(document.documentElement, { childList: true, subtree: true });
  ['shopify:section:load', 'shopify:section:reorder'].forEach(name => document.addEventListener(name, scheduleScan));
  // Establish width on the first resize; reading it here can force startup layout.
  let width;
  window.addEventListener('resize', () => {
    const nextWidth = window.innerWidth;
    if (width === nextWidth) return;
    width = nextWidth;
    nativeGalleries.forEach(element => {
      if (!element.isConnected || !isNative(element)) leaveNative(element);
      else nativeStates.get(element).move(nativeStates.get(element).index, false);
    });
    document.querySelectorAll('swiper-container').forEach(element => {
      if (isCard(element) || element.closest('.swiper-thumbnails') || !isSlider(element)) return;
      if (startNative(element)) return;
      if (observer) {
        observer.unobserve(element);
        observer.observe(element);
      } else initialize(element);
    });
  }, { passive: true });
  window.HORefreshSliders();
})();
